import React from 'react';
import styled, { css } from 'styled-components';
import { CollapsibleSubNav } from './CollapsibleSubNav';
import {
  navIconStyles,
  navTextStyles,
  sharedNavItemRootStyles,
} from '../styles';
import {
  Chip,
  ColorsV2,
  Flex,
  getToken,
  Icon,
  IconNames,
  Skeleton,
  Text,
  Tooltip,
  useThemeColors,
} from '@electricjs/arc';
import isVisible from '@/helpers/isVisible';

const TooltipWrapper = styled.div`
  cursor: default;
  &:hover {
    background: none;
  }
`;

export const navItemStyles = css`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 4.5rem;
  min-height: 4.5rem;
  line-height: 4.5rem;
  transition: color 0.2s ease;
  text-decoration: none !important;
  color: ${getToken(ColorsV2.WHITE_STATIC)};
  ${sharedNavItemRootStyles}

  span {
    color: ${getToken(ColorsV2.WHITE_STATIC)};
    ${navTextStyles}
  }

  &.active {
    background: rgba(255, 255, 255, 0.15);

    /* The component text styles overwrites the active styles, so this overwrites it again */
    span {
    }
  }

  &.ignore-active-styles {
    /* It's difficult to control the appending of the active class, */
    /* so instead if the "ignore-active-styles" class is included, we'll ignore active class properties */
    background: inherit;
    color: inherit;

    /* The component text styles overwrites the active styles, so this overwrites it again */
    span {
      background: inherit;
      color: inherit;
    }
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);

    /* The component text styles overwrites the hover styles, so this overwrites it again */
    span {
    }
  }

  ${navIconStyles}
`;

const CollapsedLabel = styled.span`
  font-size: 1rem !important;
  line-height: 1.2;
  color: inherit;
  text-align: center;
  margin-top: 0.6rem;
  display: block;
`;

const NavItemComponent = styled.a<{ $hasTooltip?: boolean }>`
  ${navItemStyles}
  ${props =>
    props.$hasTooltip &&
    css`
      cursor: not-allowed;
    `}
`;

const CustomNavItemComponent = styled(
  ({
    component: NavComponent,
    className: styledClassName,
    icon,
    chipText,
    tooltipText,
    text,
    shortText,
    visible: _visible,
    $isSideNavCollapsed: _isSideNavCollapsed,
    $toggleSideNav: _toggleSideNav,
    ...props
  }: {
    component: JSX.Element;
    className?: string;
  } & SideNavItemProps) => {
    const [iconColor] = useThemeColors([ColorsV2.WHITE_STATIC]);
    return React.cloneElement(NavComponent, {
      'data-testid': 'nav-item',
      className: `nav-item${
        NavComponent.props?.className ? ` ${NavComponent.props.className}` : ''
      }${styledClassName ? ` ${styledClassName}` : ''}`,
      ...(tooltipText && { $hasTooltip: true }),
      children: (
        <Flex width="100%" alignItems="center" justifyContent="space-between">
          <Flex flexGrow={1}>
            {icon ? (
              <Flex
                flexDirection="column"
                alignItems="center"
                data-nav-icon-label-container>
                <Icon icon={icon} color={iconColor} title={text} />
                {(shortText || text) && (
                  <CollapsedLabel data-nav-label>
                    {shortText || text}
                  </CollapsedLabel>
                )}
              </Flex>
            ) : null}
            <Text tag="span">{text}</Text>
          </Flex>
          {chipText &&
            (tooltipText ? (
              <Tooltip content={tooltipText} placement="auto-end">
                <TooltipWrapper>
                  <Flex>
                    <NavChip text={text} chipText={chipText} />
                  </Flex>
                </TooltipWrapper>
              </Tooltip>
            ) : (
              <NavChip text={text} chipText={chipText} />
            ))}
        </Flex>
      ),
      ...props,
    });
  }
)`
  ${navItemStyles}
`;

const SectionHeading = styled(Text)`
  text-transform: uppercase;
`;

export type SideNavItemProps = {
  /** Sets the skeleton loading state for this specific nav item */
  isLoading?: boolean;
  /** Changes the CSS & accessibility of the nav item if it's active */
  isActive?: boolean;
  /** Text for the nav item */
  text?: string;
  /** Link the nav item will navigate to */
  href?: string;
  /** Component used for navigation, for example a `<NavLink>` from React Router. Must be an `<a>` tag for proper styling */
  component?: JSX.Element;
  /** Custom component option. Use an `<a>` tag for proper styling. */
  customComponent?: JSX.Element;
  /** Text that represents a section heading for grouping nav items. */
  sectionHeading?: string;
  /** Optional link target (e.g. "_blank" for new tab) for the nav item */
  target?: string;
  /** Left-only icon for the nav item */
  icon?: IconNames;
  /** Array of sub nav items */
  subNavItems?: Pick<
    SideNavItemProps,
    | 'text'
    | 'href'
    | 'target'
    | 'isActive'
    | 'component'
    | 'visible'
    | 'chipText'
  >[];
  /** show or hide the nav item */
  visible?: boolean | undefined;
  /** Text for the chip */
  chipText?: string;
  /** Text for the tooltip */
  tooltipText?: string;
  /** These props are automatically passed in from SideNavBase */
  /** Function to toggle the side nav collapse state */
  $toggleSideNav?: () => void;
  /** Whether the side nav is currently collapsed */
  $isSideNavCollapsed?: boolean;
  /** Short label shown below the icon when nav is collapsed */
  shortText?: string;
};

const NavItem = (props: SideNavItemProps) => {
  const {
    isActive = false,
    text,
    href,
    component: NavComponent,
    customComponent: CustomComponent,
    sectionHeading,
    target,
    icon,
    chipText,
    tooltipText,
    shortText,
  } = props;
  const [textColor] = useThemeColors([ColorsV2.WHITE_STATIC]);

  if (sectionHeading) {
    return (
      <SectionHeading
        className="section-heading"
        intent="secondary"
        variant="legal"
        fontWeight={700}
        color={textColor}
        mt="1rem"
        mb="2rem">
        {sectionHeading}
      </SectionHeading>
    );
  }

  if (CustomComponent) return CustomComponent;

  if (NavComponent) {
    return <CustomNavItemComponent component={NavComponent} {...props} />;
  }

  return (
    <NavItemComponent
      className={`nav-item${isActive ? ' active' : ''}`}
      data-testid="nav-item"
      $hasTooltip={!!tooltipText}
      {...(isActive ? { ['aria-current']: 'page' } : {})}
      href={href}
      target={target}>
      <Flex width="100%" alignItems="center" justifyContent="space-between">
        <Flex flexGrow={1}>
          {icon ? (
            <Flex
              flexDirection="column"
              alignItems="center"
              data-nav-icon-label-container>
              <Icon icon={icon} color={textColor} title={text} />
              {(shortText || text) && (
                <CollapsedLabel data-nav-label>
                  {shortText || text}
                </CollapsedLabel>
              )}
            </Flex>
          ) : null}
          <Text tag="span">{text}</Text>
        </Flex>

        {chipText &&
          (tooltipText ? (
            <Tooltip content={tooltipText} placement="auto-end">
              <TooltipWrapper>
                <Flex>
                  <NavChip text={text} chipText={chipText} />
                </Flex>
              </TooltipWrapper>
            </Tooltip>
          ) : (
            <NavChip text={text} chipText={chipText} />
          ))}
      </Flex>
    </NavItemComponent>
  );
};

const NavChip = ({ text, chipText }: { text?: string; chipText: string }) => (
  <Chip
    id={
      text
        ? `${text.replace(' ', '').toLocaleLowerCase()}-nav-chip`
        : 'nav-chip'
    }
    backgroundColor={ColorsV2.INFO_LIGHTEST}
    variant="small">
    <Text color={ColorsV2.INFO} variant="legal">
      {chipText}
    </Text>
  </Chip>
);

export const SideNavItem = ({
  isLoading = false,
  subNavItems,
  ...navItemProps
}: SideNavItemProps): React.ReactElement => {
  if (isLoading) {
    return (
      <Skeleton
        width="23.0rem"
        height="4.5rem"
        data-testid="sidenav-item-skeleton"
      />
    );
  }
  if (subNavItems) {
    const currentPathname = window.location.pathname;
    const hasActiveSubNav = subNavItems.some(
      item =>
        (item.component && item.component?.props?.to === currentPathname) ||
        item.isActive
    );

    return (
      <CollapsibleSubNav {...navItemProps} hasActiveSubNav={hasActiveSubNav}>
        {subNavItems.filter(isVisible).map(item => (
          <NavItem key={item.text} {...item} />
        ))}
      </CollapsibleSubNav>
    );
  }
  return <NavItem {...navItemProps} />;
};
